/**
 * A lista de clientes (H17) — a tela que o menu promete desde a H01.
 *
 * ── É uma FILA DE TRABALHO, não uma agenda telefônica ─────────────────────
 *
 * A pergunta que esta tela responde não é "quais clientes existem" — é *para
 * quem eu ligo agora*. É o que decide as colunas, os recortes e a ordem por
 * último contato, em vez da alfabética: uma base de mil clientes em ordem
 * alfabética não tem primeira linha útil.
 *
 * ── Mas ela ABRE EM "TODOS" ───────────────────────────────────────────────
 *
 * O desenho propunha abrir em "Esfriando", e é uma boa tese: a fila mais
 * urgente primeiro. Na prática ela erra a primeira impressão — quem clica em
 * "Clientes" no menu espera ver os clientes, e uma tela que abre mostrando 38
 * de 1.284 parece uma base menor do que é, ou uma busca que deu errado.
 *
 * O recorte urgente continua a um clique, com a contagem à vista para quem
 * quiser ir direto. Abrir filtrado é o tipo de esperteza que economiza um
 * clique de quem já conhece a tela e cobra confiança de quem está chegando.
 *
 * ── Duas peças, e não uma encolhida ───────────────────────────────────────
 *
 * No mouse é `shared/ui/tabela`: seis colunas comparáveis, com cabeçalho que
 * ordena. Abaixo de 1024px ela dá lugar a `lista-linha--densa --controles`.
 *
 * O corte é 1024 e não 720 porque, com o menu aberto, 1024px de janela deixam
 * 720px de conteúdo — e a tabela tem piso de 1.020px. Esperar o corte de 720
 * significava exibir uma tabela impossível por trezentos pixels de largura.
 *
 * O que existia antes eram 1.940 bytes reaproveitando `lista-linha` em largura
 * cheia, e é aí que estava o defeito: `lista-linha` foi calibrada para a
 * COLUNA de conversas, com 320px. Esticada para 1.168px, ela deixava o nome à
 * esquerda, uma pastilha à direita e mil pixels de nada no meio.
 *
 * ── Os filtros são recortes de TRABALHO ───────────────────────────────────
 *
 * Cidade, origem e faixa de valor pertencem a uma busca avançada. Aqui cada
 * pílula responde a uma pergunta que alguém faz de manhã — e a contagem em
 * cada uma evita o clique de descoberta: dá para ver onde há trabalho antes de
 * filtrar.
 *
 * ── Um campo de busca, não quatro ─────────────────────────────────────────
 *
 * A pessoa que procura "Marcos" e a que cola um CPF usam a mesma caixa; quem
 * decide o que o termo é fica no servidor (D24). Quatro campos rotulados
 * obrigariam a escolher antes de digitar — e quem cola um telefone com máscara
 * erra o campo.
 *
 * ── Não há faixa de "dado atrasado" aqui, e é decisão ─────────────────────
 *
 * O carimbo de um CONJUNTO seria o mais antigo da página, e o mais antigo da
 * página é o cliente mais quieto — a conversa em que ninguém falou há uma
 * semana tem carimbo de uma semana, e isso é normal. Em qualquer base real
 * existe alguém quieto, então a faixa ficaria acesa para sempre.
 *
 * ── Os três vazios são diferentes ─────────────────────────────────────────
 *
 * "Esta revenda ainda não tem clientes" pede conectar um canal. "Nenhum
 * cliente com esse termo" pede mudar a busca. E "nenhum cliente neste recorte"
 * pede LIMPAR O FILTRO — sem esse terceiro, a pessoa que escondeu tudo com uma
 * pílula concluiria que a base está vazia. É o que a §11.1 exige distinguir.
 *
 * ── O telefone é mascarado, e a tela não tenta completar ──────────────────
 *
 * A D24 já devolve `••••7766`. A lista é onde o vazamento em volume acontece;
 * quem precisa do número abre a ficha, onde a leitura fica registrada.
 *
 * E é por isso que a dica da busca é FIXA, e não `placeholder`: vendo
 * `••••7766` a pessoa conclui que buscar por telefone não funciona. Funciona,
 * sobre o número inteiro — o mascaramento é de exibição. O placeholder sumiria
 * no primeiro caractere, exatamente quando a dúvida aparece.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { DIAS_PARA_ESFRIAR } from '@cliqcrm/contracts';
import { usePapel } from '@/shared/sessao/contexto';
import { NovoCliente } from './novo-cliente';
import { baixar } from '@/shared/lib/planilha';
import { nomeDoArquivo, planilhaDeClientes } from '../lib/planilha';
import { Carregando, Erro, Vazio } from '@/shared/ui/estados/estados';
import { Atribuir } from '@/features/atendimento/passar-para-outra-pessoa';
import { useApi, useCamposPersonalizados } from '@/shared/api/consultas';
import { useListaDeClientes, type FiltroDaLista } from '@/entities/clientes/cliente';
import { FiltroDeCampos } from '@/entities/campos/campo-personalizado';
import './clientes.css';
import {
  Botao,
  CabecalhoDeTela,
  Campo,
  Lista,
  MenuAcoes,
  Segmentado,
  Tabela,
  type ColunaTabela,
} from '@/shared/ui';
import { NumerosDaBase } from './numeros-da-base';
import { DialogoDeIniciarAtendimento } from './iniciar-atendimento';
import { useAcoesDaLinha } from './acoes-da-linha';
import { celulasDe, LinhaDeCliente } from './linha-de-cliente';
import { comSuperAdmin } from '@/entities/acesso/papel';

/** O recorte em que a tela abre, e a ordem que ele pede. */
export const FILTRO_INICIAL: FiltroDaLista = {
  busca: '',
  recorte: 'todos',
  ordenar: 'ultimo_contato',
  direcao: 'desc',
};
/*
 * Os cinco recortes, com o texto que cada um usa quando volta vazio.
 *
 * O texto do vazio mora AQUI, junto do rótulo, porque os dois falam do mesmo
 * recorte: separá-los é como se acaba com uma pílula chamada "Esfriando" e um
 * vazio falando de responsável.
 */
const RECORTES = [
  {
    chave: 'esfriando',
    rotulo: 'Esfriando',
    porque: `Ninguém está negociando há mais de ${DIAS_PARA_ESFRIAR} dias sem falar com você — e é isso que esta fila procura.`,
  },
  { chave: 'meus', rotulo: 'Meus clientes', porque: 'Nenhuma conversa está atribuída a você.' },
  {
    chave: 'sem_responsavel',
    rotulo: 'Sem responsável',
    porque: 'Todo cliente com conversa já tem alguém atrás dele.',
  },
  {
    chave: 'com_negocio',
    rotulo: 'Com oportunidade aberta',
    porque: 'Nenhuma oportunidade em aberto no seu escopo.',
  },
  { chave: 'todos', rotulo: 'Todos', porque: 'A base está vazia dentro do seu escopo.' },
] as const satisfies readonly {
  chave: FiltroDaLista['recorte'];
  rotulo: string;
  porque: string;
}[];
/**
 * As colunas (desenho de 26/10/2026).
 *
 * **As etiquetas moram embaixo do nome**, e não numa coluna: são o assunto de
 * quem é a pessoa, e lidas junto do nome dizem mais que numa coluna à parte.
 *
 * **Contato junta e-mail e telefone**, um por linha, mascarados como vêm.
 *
 * **A etapa vai em chip, com a cor que ela tem no funil.** O desenho anterior a
 * deixava em texto para não encher a tela de roxo; com a cor de cada etapa,
 * o chip deixa de ser enfeite e passa a agrupar: a mesma etapa tem a mesma cor
 * em toda linha, e o funil se lê de relance.
 *
 * O valor saiu da tabela: continua na ficha e no celular, e no quadro do funil.
 */
const COLUNAS: ColunaTabela[] = [
  {
    chave: 'cliente',
    rotulo: 'Cliente',
    classeDaColuna: 'clientes__col-cliente',
    ordenavel: true,
  },
  { chave: 'meios', rotulo: 'Contato', classeDaColuna: 'clientes__col-meios' },
  { chave: 'responsavel', rotulo: 'Responsável', classeDaColuna: 'clientes__col-responsavel' },
  { chave: 'etapa', rotulo: 'Etapa', classeDaColuna: 'clientes__col-etapa' },
  {
    chave: 'contato',
    rotulo: 'Último contato',
    classeDaColuna: 'clientes__col-contato',
    ordenavel: true,
  },
  {
    chave: 'acoes',
    rotulo: 'Ações',
    classeDaColuna: 'clientes__col-acoes',
  },
];
/** A coluna da tabela, e a ordem que a API entende. Só três ordenam. */
const ORDEM_POR_COLUNA: Record<string, FiltroDaLista['ordenar']> = {
  cliente: 'nome',
  contato: 'ultimo_contato',
  valor: 'valor',
};

const COLUNA_POR_ORDEM: Record<FiltroDaLista['ordenar'], string> = {
  nome: 'cliente',
  ultimo_contato: 'contato',
  valor: 'valor',
};

/**
 * Quem vê cada ação do topo.
 *
 * ESCONDE, não desabilita. A regra do `Botao` é que desabilitado exige motivo,
 * e ela vale para o que a pessoa poderia fazer noutro momento — um envio sem
 * texto, um salvamento em curso. Papel é outra coisa: um vendedor nunca vai
 * poder exportar, e um botão permanentemente apagado dizendo "você não pode" é
 * ruído todo dia, para sempre.
 *
 * Isto é conveniência de tela, e não segurança: quem chamar a rota direto é
 * recusado pela API do mesmo jeito. A matriz da §13.2 é a que manda.
 */
const CADASTRA = comSuperAdmin(['vendedor', 'gestor', 'admin_tenant']);
const EXPORTA = comSuperAdmin(['admin_tenant']);
/** A direção que cada ordem pede quando ninguém escolheu — a mesma da API. */
const DIRECAO_PADRAO: Record<FiltroDaLista['ordenar'], 'asc' | 'desc'> = {
  nome: 'asc',
  ultimo_contato: 'desc',
  valor: 'desc',
};

export function ListaDeClientes({
  filtro,
  aoFiltrar,
  aoAbrirCliente,
  aoAbrirConversa,
}: {
  /*
   * O filtro vem DE FORA, e é o que resolve a Armadilha da task.
   *
   * "Perder a busca ao voltar da ficha. Quem procura um cliente, abre, confere
   * e volta espera encontrar a lista como deixou." Com o estado aqui dentro,
   * desmontar a tela para mostrar a ficha o apagaria — e refazer a busca a
   * cada consulta é o que faz alguém abrir a ficha em outra aba e nunca mais
   * usar a lista.
   *
   * Vale para o recorte e a ordem pelo mesmo motivo, e mais forte: voltar da
   * ficha para "Todos" ordenado por data desfaz um trabalho de triagem que a
   * pessoa acabou de montar.
   */
  filtro: FiltroDaLista;
  aoFiltrar: (filtro: FiltroDaLista) => void;
  aoAbrirCliente: (clienteId: string) => void;
  aoAbrirConversa: (conversaId: string) => void;
}): ReactNode {
  const lista = useListaDeClientes(filtro);
  // O catálogo de campos do cliente (#568), para o "Filtrar por campo".
  const camposDoCliente = useCamposPersonalizados('cliente');
  const api = useApi();
  const papel = usePapel();
  const rolagem = useRef<HTMLDivElement>(null);
  const dica = useId();
  const [buscaEmFoco, definirBuscaEmFoco] = useState(false);
  const [cadastrando, definirCadastrando] = useState(false);
  const [exportando, definirExportando] = useState(false);
  const [recadoDoTopo, definirRecadoDoTopo] = useState<string | null>(null);

  /*
   * A exportação pede o MESMO recorte e a MESMA busca da tela.
   *
   * Quem exporta acabou de olhar a lista, e o arquivo precisa conter o que ela
   * mostrava. Um arquivo com a base inteira para quem estava vendo trinta
   * linhas é um erro que ninguém confere — são mil linhas.
   */
  const exportar = useCallback(() => {
    if (!api || exportando) return;
    definirExportando(true);

    void api
      .exportarClientes({
        consulta: {
          ...(filtro.busca.trim() !== '' ? { busca: filtro.busca.trim() } : {}),
          recorte: filtro.recorte,
          // A planilha sai com o MESMO filtro por campo da tela (#568).
          ...(filtro.campos ? { campos: filtro.campos } : {}),
        },
      })
      .then((arquivo) => {
        baixar(planilhaDeClientes(arquivo.itens, arquivo.campos), nomeDoArquivo(), 'Clientes');
        definirRecadoDoTopo(
          arquivo.truncada_em === null
            ? `Planilha com ${arquivo.itens.length} clientes.`
            : // Truncada em silêncio é pior que recusa: quem recebe acha que
              // tem a base inteira e age sobre uma lista incompleta.
              `Planilha com os primeiros ${arquivo.truncada_em} clientes — o recorte tem mais. Filtre antes de exportar de novo.`,
        );
      })
      .catch(() =>
        definirRecadoDoTopo('Não foi possível gerar a planilha. Tente de novo em um instante.'),
      )
      .finally(() => definirExportando(false));
  }, [api, exportando, filtro.busca, filtro.recorte, filtro.campos]);

  /*
   * A rolagem contínua por observação, e não por botão.
   *
   * A sentinela é um elemento vazio no fim da lista: quando ele entra na
   * janela, a próxima página é pedida. Um botão "carregar mais" faria a pessoa
   * clicar a cada trinta clientes numa base de mil.
   */
  const sentinela = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const alvo = sentinela.current;
    if (!alvo || !lista.temMais) return undefined;

    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((entrada) => entrada.isIntersecting)) lista.carregarMais();
      },
      // A margem pede a página ANTES de o fim aparecer: sem ela, a lista para
      // por um instante em cada página, e a rolagem engasga.
      { root: rolagem.current, rootMargin: '200px' },
    );

    observador.observe(alvo);
    return () => observador.disconnect();
  }, [lista.temMais, lista.carregarMais, lista.itens.length]);

  /*
   * Clicar na mesma coluna INVERTE; clicar em outra começa pelo padrão dela.
   *
   * Herdar a direção anterior daria "nome, decrescente" para quem só queria
   * ver a lista por nome — alfabético ao contrário não é ordem que alguém
   * peça, e a pessoa clicaria de novo para desfazer.
   */
  const ordenarPor = useCallback(
    (coluna: string) => {
      const ordem = ORDEM_POR_COLUNA[coluna];
      if (!ordem) return;

      aoFiltrar({
        ...filtro,
        ordenar: ordem,
        direcao:
          filtro.ordenar === ordem
            ? filtro.direcao === 'asc'
              ? 'desc'
              : 'asc'
            : DIRECAO_PADRAO[ordem],
      });
    },
    [filtro, aoFiltrar],
  );

  const buscando = filtro.busca.trim() !== '';
  const recorteAtual = RECORTES.find((r) => r.chave === filtro.recorte) ?? RECORTES[4];
  const base = lista.contagens?.todos ?? 0;

  const acoes = useAcoesDaLinha(aoAbrirCliente, aoAbrirConversa);

  /*
   * A seleção de linhas (26/10/2026). Só dos clientes que estão na tela: trocar
   * o recorte ou a busca limpa, porque uma marca em quem sumiu da lista seria
   * uma ação sobre alguém que a pessoa já não vê.
   */
  const [marcados, definirMarcados] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    definirMarcados(new Set());
  }, [filtro.recorte, filtro.busca, filtro.campos]);
  const visiveis = useMemo(() => lista.itens.map((c) => c.id), [lista.itens]);
  const alternarMarcado = useCallback((id: string) => {
    definirMarcados((antes) => {
      const depois = new Set(antes);
      if (depois.has(id)) depois.delete(id);
      else depois.add(id);
      return depois;
    });
  }, []);
  const alternarTodos = useCallback(() => {
    definirMarcados((antes) =>
      visiveis.every((id) => antes.has(id)) ? new Set() : new Set(visiveis),
    );
  }, [visiveis]);

  return (
    <section className="clientes" aria-label="Clientes">
      <CabecalhoDeTela
        titulo="Clientes"
        apoio="Todo mundo que já falou com a organização, e de quem é cada um."
        {...(lista.carregandoPrimeira
          ? {}
          : {
              /*
               * "38 de 1.284" responde antes de a dúvida existir.
               *
               * Um número grande ao lado de uma lista curta faz a pessoa
               * procurar o que falta; o par diz que o recorte é dela, e a
               * descrição diz que o total já é o do escopo.
               */
              contagem: `${lista.total.toLocaleString('pt-BR')} de ${base.toLocaleString('pt-BR')}`,
              descricaoDaContagem: 'Dentro do seu escopo',
            })}
        {...(papel && (CADASTRA.has(papel) || EXPORTA.has(papel))
          ? {
              acoes: (
                <>
                  {/*
                    O invólucro leva a classe, e não o botão: `Botao` não aceita
                    `className` de propósito — é o que impede uma tela de
                    redesenhar o componente por dentro. Esconder no celular é
                    posicionamento, e posicionamento é de quem usa.
                  */}
                  {papel && EXPORTA.has(papel) && (
                    <span className="clientes__exportar">
                      <Botao
                        variante="secundario"
                        type="button"
                        onClick={exportar}
                        desabilitado={exportando}
                        motivo="Gerando a planilha…"
                      >
                        {exportando ? 'Gerando…' : 'Exportar'}
                      </Botao>
                    </span>
                  )}
                  {papel && CADASTRA.has(papel) && (
                    <Botao
                      variante="primario"
                      type="button"
                      onClick={() => definirCadastrando(true)}
                    >
                      Novo cliente
                    </Botao>
                  )}
                  {/*
                    No celular "Exportar" sai da fileira e vira item deste menu,
                    que é o padrão do projeto para ação secundária no telefone —
                    assim o título e "Novo cliente" cabem na mesma linha. No
                    mouse ele seria um menu de um item só, e o CSS o esconde.
                  */}
                  {papel && EXPORTA.has(papel) && (
                    <span className="clientes__mais">
                      <MenuAcoes
                        rotulo="Mais ações da lista"
                        itens={[
                          {
                            rotulo: exportando ? 'Gerando a planilha…' : 'Exportar',
                            aoClicar: exportar,
                            ...(exportando
                              ? { desabilitado: true, motivo: 'Gerando a planilha…' }
                              : {}),
                          },
                        ]}
                      />
                    </span>
                  )}
                </>
              ),
            }
          : {})}
      />

      <NumerosDaBase
        contagens={lista.contagens}
        recorte={filtro.recorte}
        aoEscolher={(recorte) => aoFiltrar({ ...filtro, recorte })}
      />

      <div className="clientes__barra">
        <div className="clientes__busca">
          <Campo
            type="search"
            rotuloOculto="Buscar cliente"
            value={filtro.busca}
            onChange={(evento) => aoFiltrar({ ...filtro, busca: evento.target.value })}
            onFocus={() => definirBuscaEmFoco(true)}
            onBlur={() => definirBuscaEmFoco(false)}
            placeholder="Buscar cliente"
            aria-describedby={dica}
          />
          {/*
            A dica esconde da VISTA e do leitor de tela ao mesmo tempo.
            Ela é o `aria-describedby` do campo, e descrever algo que a pessoa
            não pode conferir é pior que não descrever.
          */}
          <p
            className="clientes__dica"
            id={dica}
            hidden={!buscaEmFoco && !buscando}
            aria-hidden={!buscaEmFoco && !buscando}
          >
            Nome, e-mail, documento ou o telefone inteiro — mesmo com a lista mostrando só os quatro
            últimos dígitos.
          </p>
        </div>

        {/*
          Quatro recortes nas abas; "Esfriando" é o cartão do topo, que filtra
          igual (o segmentado é de duas a quatro opções).
        */}
        <div className="clientes__filtros">
          <Segmentado
            como="grupo"
            rotulo="Recortes de trabalho"
            escolhida={filtro.recorte}
            aoEscolher={(recorte) => aoFiltrar({ ...filtro, recorte })}
            opcoes={RECORTES.filter((r) => r.chave !== 'esfriando').map((r) => ({
              valor: r.chave,
              rotulo: r.rotulo,
              ...(lista.contagens ? { contagem: lista.contagens[r.chave] } : {}),
            }))}
          />
        </div>
        <FiltroDeCampos
          id="clientes-filtro"
          campos={camposDoCliente.data?.itens ?? []}
          filtro={filtro.campos ?? ''}
          aoMudar={(campos) => aoFiltrar({ ...filtro, campos })}
        />
      </div>

      {/*
        O desfecho da tarefa é ANUNCIADO, e fica onde a ação foi pedida.
        Uma tarefa criada num menu de linha não deixa rastro nenhum na lista:
        sem esta linha, a pessoa clica de novo achando que não pegou.
      */}
      {(acoes.recado ?? recadoDoTopo) && (
        <p className="clientes__recado" role="status">
          {acoes.recado ?? recadoDoTopo}
        </p>
      )}

      <div className="clientes__rolagem" ref={rolagem}>
        {lista.erro != null && lista.itens.length === 0 ? (
          <Erro
            erro={lista.erro}
            aoRepetir={lista.recarregar}
            titulo="Não foi possível carregar os clientes"
          />
        ) : lista.carregandoPrimeira ? (
          <Carregando forma="lista" linhas={8} rotulo="Carregando os clientes" />
        ) : lista.itens.length === 0 ? (
          /*
           * Os TRÊS vazios, com saídas diferentes — ver o cabeçalho.
           *
           * O do recorte nomeia o corte e diz quantos clientes a base tem, e a
           * saída é limpar o filtro. "Nenhum cliente" faria a pessoa achar que
           * a base está vazia quando ela mesma escondeu tudo.
           */
          buscando ? (
            <Vazio
              titulo="Nenhum cliente com esse termo"
              porque="Tente outro trecho do nome, o telefone com ou sem máscara, o e-mail ou o documento. A busca procura nos quatro."
              acao={{
                rotulo: 'Limpar a busca',
                aoClicar: () => aoFiltrar({ ...filtro, busca: '' }),
              }}
            />
          ) : filtro.recorte !== 'todos' ? (
            <Vazio
              titulo={`Nenhum cliente em "${recorteAtual.rotulo}"`}
              porque={`${recorteAtual.porque} A base tem ${base.toLocaleString('pt-BR')} ${
                base === 1 ? 'cliente' : 'clientes'
              } dentro do seu escopo.`}
              acao={{
                rotulo: 'Ver todos',
                aoClicar: () => aoFiltrar({ ...filtro, recorte: 'todos' }),
              }}
            />
          ) : (
            <Vazio
              titulo="Esta organização ainda não tem clientes"
              porque="Cliente nasce de conversa ou de conector: quando alguém escrever por um canal conectado, ele aparece aqui sozinho."
            />
          )
        ) : (
          <>
            {marcados.size > 0 && (
              <div className="clientes__selecao" role="status">
                <span>
                  {marcados.size === 1
                    ? '1 cliente selecionado'
                    : `${marcados.size} clientes selecionados`}
                </span>
                <Botao variante="fantasma" miudo onClick={() => definirMarcados(new Set())}>
                  Limpar a seleção
                </Botao>
              </div>
            )}
            <Tabela
              classe="clientes__tabela-rolagem"
              classeDaTabela="clientes__tabela"
              rotulo="Clientes"
              densa
              colunas={COLUNAS}
              ordem={{ chave: COLUNA_POR_ORDEM[filtro.ordenar], direcao: filtro.direcao }}
              aoOrdenar={ordenarPor}
              chaveDe={(linha, indice) => String(linha['id'] ?? indice)}
              linhas={lista.itens.map((cliente) => celulasDe(cliente, acoes))}
              // Clicar em qualquer lugar da linha abre o Cliente 360, como no celular.
              aoAbrirLinha={(linha) => aoAbrirCliente(String(linha['id']))}
              selecao={{
                marcadas: marcados,
                aoAlternar: alternarMarcado,
                aoAlternarTodas: alternarTodos,
                rotuloDe: (linha) => `Selecionar ${String(linha['nome'])}`,
                rotuloDeTodas: 'Selecionar todos os clientes da lista',
              }}
            />

            {/*
              O invólucro é da TELA, e é o que troca uma peça pela outra.
              `Lista` desenha `<ul class="lista">` e nada mais: quem decide que
              esta lista some acima de 1024px é esta tela, e uma classe de tela
              dentro do componente seria o componente sabendo onde está.
            */}
            <div className="clientes__lista">
              <Lista fina>
                {lista.itens.map((cliente) => (
                  <LinhaDeCliente key={cliente.id} cliente={cliente} acoes={acoes} />
                ))}
              </Lista>
            </div>

            <div ref={sentinela} className="lista__sentinela" aria-hidden="true" />

            {/* Só na rolagem: a primeira página já tem o esqueleto acima, e
                dois indicadores ao mesmo tempo leem como dois carregamentos. */}
            {lista.carregando ? (
              <p className="clientes__fim" role="status">
                Carregando mais…
              </p>
            ) : (
              <p className="clientes__fim">
                {`Mostrando ${lista.itens.length.toLocaleString('pt-BR')} de ${lista.total.toLocaleString('pt-BR')} ${
                  lista.total === 1 ? 'cliente' : 'clientes'
                }`}
                {!lista.temMais && lista.total > 0 ? ' · fim da lista' : ''}
              </p>
            )}
          </>
        )}
      </div>

      {/*
        UM diálogo para a tela inteira, e não um por linha.
        Por linha, sete clientes montariam sete diálogos fechados — cada um com
        o próprio estado e a própria chave de idempotência — e o React
        remontaria todos a cada rolagem.
      */}
      {acoes.passando && (
        <Atribuir
          alvo={{ tipo: 'conversa', id: acoes.passando }}
          responsavelAtual={null}
          aberto
          aoFechar={acoes.fecharPassagem}
        />
      )}

      {acoes.contatando && (
        <DialogoDeIniciarAtendimento
          cliente={{
            nome: acoes.contatando.nome_exibicao,
            telefone: acoes.contatando.telefone,
          }}
          salvando={acoes.enviandoContato}
          erro={acoes.erroDoContato}
          aoEnviar={acoes.enviarPrimeiroContato}
          aoFechar={acoes.fecharContato}
        />
      )}

      {cadastrando && (
        <NovoCliente
          aoFechar={() => definirCadastrando(false)}
          aoCriar={(id) => {
            definirCadastrando(false);
            /*
             * A ficha ABRE, e a lista não só recarrega.
             *
             * A tela abre em "Esfriando", onde um cliente recém-criado não
             * entra: ele não tem negócio aberto. Voltar para uma lista em que
             * nada mudou faria o cadastro parecer não ter acontecido.
             */
            aoAbrirCliente(id);
          }}
        />
      )}
    </section>
  );
}
